import api from './api'

export const patientService = {
  list: async (params = {}) => {
    const response = await api.get('/api/patients', { params })
    return response.data
  },
  dashboard: async (params = {}) => {
    const response = await api.get('/api/patients/dashboard', { params })
    return response.data
  },
  temperatureSeries: async (patientId) => {
    const response = await api.get('/api/patients/dashboard/temperature', { params: { patientId } })
    return response.data
  },
  dashboardActivity: async (params = {}) => {
    const response = await api.get('/api/patients/dashboard/activity', { params })
    return response.data
  },
  shifts: async (params = {}) => {
    const response = await api.get('/api/patients/shifts', { params })
    return response.data
  },
  shiftStatus: async () => {
    const response = await api.get('/api/patients/shift')
    return response.data
  },
  startShift: async (patientId) => {
    const response = await api.post('/api/patients/shift/start', { patientId })
    return response.data
  },
  endShift: async () => {
    const response = await api.post('/api/patients/shift/end')
    return response.data
  },
  getById: async (id) => {
    const response = await api.get(`/api/patients/${id}`)
    return response.data
  },
  clinicalDocument: async (id, params) => {
    const response = await api.get(`/api/patients/${id}/clinical-document`, {
      params,
      responseType: 'blob'
    })
    return response
  },
  setViewers: async (id, userIds) => {
    const response = await api.put(`/api/patients/${id}/viewers`, { userIds })
    return response.data
  },
  create: async (data) => {
    const response = await api.post('/api/patients', data)
    return response.data
  },
  update: async (id, data) => {
    const response = await api.patch(`/api/patients/${id}`, data)
    return response.data
  },
  updateContacts: async (id, data) => {
    const response = await api.patch(`/api/patients/${id}/contacts`, data)
    return response.data
  },
  archive: async (id, data) => {
    const response = await api.post(`/api/patients/${id}/archive`, data)
    return response.data
  },
  remove: async (id) => {
    const response = await api.delete(`/api/patients/${id}`)
    return response.data
  },
  addDocument: async (id, file, title) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('title', title)
    const response = await api.post(`/api/patients/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },
  deleteDocument: async (id, documentId) => {
    const response = await api.delete(`/api/patients/${id}/documents/${documentId}`)
    return response.data
  },
  staff: async () => {
    const response = await api.get('/api/patients/staff')
    return response.data
  },
  customers: async (search) => {
    const response = await api.get('/api/patients/customers', { params: { search } })
    return response.data
  },
  addAllergy: async (id, description) => {
    const response = await api.post(`/api/patients/${id}/allergies`, { description })
    return response.data
  },
  deleteAllergy: async (id, allergyId) => {
    const response = await api.delete(`/api/patients/${id}/allergies/${allergyId}`)
    return response.data
  },
  addAlert: async (id, description) => {
    const response = await api.post(`/api/patients/${id}/alerts`, { description })
    return response.data
  },
  deleteAlert: async (id, alertId) => {
    const response = await api.delete(`/api/patients/${id}/alerts/${alertId}`)
    return response.data
  },
  addMeasure: async (id, data) => {
    const response = await api.post(`/api/patients/${id}/measures`, data)
    return response.data
  },
  addVital: async (id, data) => {
    const response = await api.post(`/api/patients/${id}/vitals`, data)
    return response.data
  },
  updateVital: async (id, vitalId, data) => {
    const response = await api.patch(`/api/patients/${id}/vitals/${vitalId}`, data)
    return response.data
  },
  addMedication: async (id, data) => {
    const response = await api.post(`/api/patients/${id}/medications`, data)
    return response.data
  },
  updateMedication: async (id, medicationId, status) => {
    const response = await api.patch(`/api/patients/${id}/medications/${medicationId}`, { status })
    return response.data
  },
  medicationDay: async (id, date) => {
    const response = await api.get(`/api/patients/${id}/medications/day`, { params: { date } })
    return response.data
  },
  createPrescription: async (id, data) => {
    const response = await api.post(`/api/patients/${id}/prescriptions`, data)
    return response.data
  },
  updatePrescription: async (id, prescriptionId, data) => {
    const response = await api.patch(`/api/patients/${id}/prescriptions/${prescriptionId}`, data)
    return response.data
  },
  suspendPrescription: async (id, prescriptionId, reason) => {
    const response = await api.post(`/api/patients/${id}/prescriptions/${prescriptionId}/suspend`, { reason })
    return response.data
  },
  reloadPrescriptionStock: async (id, prescriptionId, quantity) => {
    const response = await api.post(`/api/patients/${id}/prescriptions/${prescriptionId}/stock`, { quantity })
    return response.data
  },
  administerSchedule: async (id, scheduleId) => {
    const response = await api.post(`/api/patients/${id}/schedules/${scheduleId}/administer`)
    return response.data
  },
  refuseSchedule: async (id, scheduleId, reason) => {
    const response = await api.post(`/api/patients/${id}/schedules/${scheduleId}/refuse`, { reason })
    return response.data
  },
  administerPrn: async (id, prescriptionId, reason) => {
    const response = await api.post(`/api/patients/${id}/prescriptions/${prescriptionId}/prn`, { reason })
    return response.data
  },
  evolutionRoles: async () => {
    const response = await api.get('/api/patients/evolution-roles')
    return response.data
  },
  addEvolutionRole: async (name) => {
    const response = await api.post('/api/patients/evolution-roles', { name })
    return response.data
  },
  addEvolution: async (id, data) => {
    const response = await api.post(`/api/patients/${id}/evolutions`, data)
    return response.data
  },
  notes: async (id, { scope = 'today', before } = {}) => {
    const response = await api.get(`/api/patients/${id}/notes`, { params: { scope, before: before || undefined } })
    return response.data
  },
  addNote: async (id, body) => {
    const response = await api.post(`/api/patients/${id}/notes`, { body })
    return response.data
  },
  updateNote: async (id, noteId, body) => {
    const response = await api.patch(`/api/patients/${id}/notes/${noteId}`, { body })
    return response.data
  },
  deleteNote: async (id, noteId) => {
    const response = await api.delete(`/api/patients/${id}/notes/${noteId}`)
    return response.data
  },
  addPhoto: async (id, file) => {
    const formData = new FormData()
    formData.append('photo', file, file.name || 'foto.jpg')
    const response = await api.post(`/api/patients/${id}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },
  deletePhoto: async (id, photoId) => {
    const response = await api.delete(`/api/patients/${id}/photos/${photoId}`)
    return response.data
  },
  setPrimaryPhoto: async (id, photoId) => {
    const response = await api.patch(`/api/patients/${id}/photos/${photoId}/primary`)
    return response.data
  },
  financialCategories: async () => {
    const response = await api.get('/api/patients/financial-categories')
    return response.data
  },
  addCharge: async (id, data) => {
    const response = await api.post(`/api/patients/${id}/charges`, data)
    return response.data
  },
  updateChargeStatus: async (id, chargeId, status) => {
    const response = await api.patch(`/api/patients/${id}/charges/${chargeId}/status`, { status })
    return response.data
  },
  getTherapy: async (id) => {
    const response = await api.get(`/api/patients/${id}/therapy`)
    return response.data
  },
  addTherapyActivity: async (id, data) => {
    const response = await api.post(`/api/patients/${id}/therapy/activities`, data)
    return response.data
  },
  updateTherapyActivity: async (id, activityId, data) => {
    const response = await api.patch(`/api/patients/${id}/therapy/activities/${activityId}`, data)
    return response.data
  },
  addTherapyGoal: async (id, data) => {
    const response = await api.post(`/api/patients/${id}/therapy/goals`, data)
    return response.data
  },
  updateTherapyGoal: async (id, goalId, data) => {
    const response = await api.patch(`/api/patients/${id}/therapy/goals/${goalId}`, data)
    return response.data
  },
  addTherapySession: async (id, data) => {
    const response = await api.post(`/api/patients/${id}/therapy/sessions`, data)
    return response.data
  },
  nutritionDay: async (id, date) => {
    const response = await api.get(`/api/patients/${id}/nutrition/day`, { params: { date } })
    return response.data
  },
  createNutritionPrescription: async (id, data) => {
    const response = await api.post(`/api/patients/${id}/nutrition/prescriptions`, data)
    return response.data
  },
  updateNutritionPrescription: async (id, prescriptionId, data) => {
    const response = await api.patch(`/api/patients/${id}/nutrition/prescriptions/${prescriptionId}`, data)
    return response.data
  },
  placeNutritionPrescription: async (id, prescriptionId, data) => {
    const response = await api.post(`/api/patients/${id}/nutrition/prescriptions/${prescriptionId}/place`, data)
    return response.data
  },
  suspendNutritionPrescription: async (id, prescriptionId, reason) => {
    const response = await api.post(`/api/patients/${id}/nutrition/prescriptions/${prescriptionId}/suspend`, { reason })
    return response.data
  },
  reloadNutritionStock: async (id, prescriptionId, quantity) => {
    const response = await api.post(`/api/patients/${id}/nutrition/prescriptions/${prescriptionId}/stock`, { quantity })
    return response.data
  },
  recordNutritionSchedule: async (id, scheduleId, data) => {
    const response = await api.post(`/api/patients/${id}/nutrition/schedules/${scheduleId}/record`, data)
    return response.data
  },
  addNutritionHydration: async (id, data) => {
    const response = await api.post(`/api/patients/${id}/nutrition/hydration`, data)
    return response.data
  },
  deleteNutritionHydration: async (id, hydrationId) => {
    const response = await api.delete(`/api/patients/${id}/nutrition/hydration/${hydrationId}`)
    return response.data
  },
  updateNutritionGoal: async (id, waterGoalMl) => {
    const response = await api.patch(`/api/patients/${id}/nutrition/goal`, { waterGoalMl })
    return response.data
  }
}
